// VOYA9 brand color tokens — used both via Tailwind classes and inline styles.
export const C = {
  cream: "#FAF6F0",
  bone: "#F0E9DD",
  sand: "#E8DFD0",
  pure: "#FFFFFF",
  ink: "#0F0E0D",
  warmBlack: "#1C1917",
  warmGray: "#78716C",
  warmGray2: "#A8A29E",
  warmGray3: "#D6D3D1",
  warmGray4: "#E7E5E4",
  peach: "#F4C9A8",
  peachDeep: "#D88B5C",
  peachDark: "#A85530",
  sage: "#B8C9A6",
  sageDeep: "#5C7A4A",
  sageDark: "#2F4423",
  ochre: "#E8C66B",
  ochreDeep: "#A07A1F",
  ochreDark: "#5C440F",
  lavender: "#C8BFD9",
  lavenderDeep: "#5C4F7C",
  blue: "#A8C0D9",
  blueDeep: "#3D5A75",
  coral: "#D85A3A",
};

// 6 program slugs + display metadata. Used for landing categories grid +
// "Coming soon" overlays. Active flag flips based on backend programs list.
// Order matches the live marketing API sortOrder.
export const PROGRAM_META = [
  { num: "01", slugMatch: ["weight-management", "weight"], t: "WEIGHT", t2: "MANAGEMENT", q: "Your metabolism, reimagined.", color: C.peach, dark: C.peachDark },
  { num: "02", slugMatch: ["recovery-healing", "recovery", "healing"], t: "RECOVERY", t2: "& HEALING", q: "Heal smarter. Move better.", color: C.sage, dark: C.sageDark },
  { num: "03", slugMatch: ["skin-beauty", "skin", "beauty"], t: "SKIN &", t2: "BEAUTY", q: "Glow from the inside.", color: C.ochre, dark: C.ochreDark },
  { num: "04", slugMatch: ["longevity"], t: "LONGEVITY", t2: "", q: "Age is just a number. Make it a low one.", color: C.lavender, dark: "#2D1F4A" },
  { num: "05", slugMatch: ["cognitive-performance", "cognitive"], t: "COGNITIVE", t2: "PERFORMANCE", q: "Sharper focus. Steadier mind.", color: C.blue, dark: "#1F3349" },
  { num: "06", slugMatch: ["energy-vitality", "energy", "vitality", "hormone", "hormone-optimization"], t: "ENERGY &", t2: "VITALITY", q: "Get your edge back.", color: "#D9D2C7", dark: "#2D2820" },
  // Sexual Wellness is a "quiet active" program — purchasable but not
  // surfaced on landing/browse grids. Entry here is for the program
  // detail surfaces (color scheme + tagline + placeholder fallback)
  // when a member arrives via direct URL, search, or cross-program
  // routing. Discreet dusty-rose palette — clinical, not promotional.
  { num: "—", slugMatch: ["sexual-wellness", "sexual", "libido", "pt-141", "bremelanotide"], t: "SEXUAL", t2: "WELLNESS", q: "Biology you can trust.", color: "#D9B8B0", dark: "#5C2D26" },
];

export function metaForSlug(slug) {
  const s = (slug || "").toLowerCase();
  return (
    PROGRAM_META.find((m) => m.slugMatch.some((x) => s.includes(x))) ||
    PROGRAM_META[0]
  );
}

// Per-program color schemes for the full-bleed color-tile treatment.
// Sourced from the marketing site so the member area stays brand-consistent.
// Backend should ultimately return a `colors` object on the program model;
// this is the slug-keyed fallback when the backend hasn't shipped that yet.
export const PROGRAM_COLOR_SCHEMES = {
  weight: {
    background: "#F4C9A8",
    textPrimary: "#6B2A0F",
    textSecondary: "#A85530",
    numberOverlay: "#D88B5C",
    borderColor: "rgba(168, 85, 48, 0.25)",
  },
  recovery: {
    background: "#B8C9A6",
    textPrimary: "#2F4423",
    textSecondary: "#5C7A4A",
    numberOverlay: "#5C7A4A",
    borderColor: "rgba(47, 68, 35, 0.25)",
  },
  longevity: {
    background: "#C8C2DC",
    textPrimary: "#3D2D5C",
    textSecondary: "#6B5994",
    numberOverlay: "#6B5994",
    borderColor: "rgba(61, 45, 92, 0.25)",
  },
  cognitive: {
    background: "#A8B5D6",
    textPrimary: "#2D3E5C",
    textSecondary: "#5C6E94",
    numberOverlay: "#5C6E94",
    borderColor: "rgba(45, 62, 92, 0.25)",
  },
  skin: {
    background: "#E8D352",
    textPrimary: "#4A3F0A",
    textSecondary: "#6B5C0E",
    numberOverlay: "#8C7912",
    borderColor: "rgba(74, 63, 10, 0.25)",
  },
  energy: {
    background: "#C2B59E",
    textPrimary: "#3D3327",
    textSecondary: "#6B5C45",
    numberOverlay: "#6B5C45",
    borderColor: "rgba(61, 51, 39, 0.25)",
  },
  sexual: {
    background: "#D9B8B0",
    textPrimary: "#5C2D26",
    textSecondary: "#8C4D44",
    numberOverlay: "#8C4D44",
    borderColor: "rgba(92, 45, 38, 0.25)",
  },
};

// Resolve a color scheme for a program. Prefers `program.colors` (plural
// object) from the backend; falls back to synthesizing from flat
// `program.color/accentColor/darkColor` fields if those are present;
// otherwise falls back to a slug-keyed match against PROGRAM_COLOR_SCHEMES.
export function colorSchemeForProgram(program) {
  if (program && program.colors && program.colors.background) return program.colors;
  if (program && program.color) {
    // Synthesize a colors object from the flat fields the backend returns
    // today. textPrimary/textSecondary use accentColor/darkColor when
    // available so contrast stays in spec.
    const dark = program.darkColor || program.accentColor || "#0F0E0D";
    const accent = program.accentColor || program.darkColor || dark;
    return {
      background: program.color,
      textPrimary: program.darkColor || dark,
      textSecondary: accent,
      numberOverlay: accent,
      borderColor: hexToRgba(dark, 0.25),
    };
  }
  const slug = (program?.slug || "").toLowerCase();
  for (const key of Object.keys(PROGRAM_COLOR_SCHEMES)) {
    if (slug.includes(key)) return PROGRAM_COLOR_SCHEMES[key];
  }
  return PROGRAM_COLOR_SCHEMES.weight; // sensible default
}

function hexToRgba(hex, alpha) {
  if (!hex) return `rgba(15,14,13,${alpha})`;
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Resolve a numbered category index for a program. Prefers explicit fields
// the backend may set (`categoryNumber` / `displayOrder`); otherwise infers
// from PROGRAM_META.
export function categoryNumberForProgram(program) {
  if (program?.categoryNumber) return String(program.categoryNumber).padStart(2, "0");
  if (program?.displayOrder) return String(program.displayOrder).padStart(2, "0");
  const slug = (program?.slug || "").toLowerCase();
  const meta = PROGRAM_META.find((m) => m.slugMatch.some((x) => slug.includes(x)));
  return meta?.num || "01";
}
